/**
 * @module app.pp
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable, observable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, StandardLoadOptions, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { AuthService, IAuthSearchFilters } from 'app/um/services/auth-service';
import { IAuthSearchOptions } from 'app/um/components/auth/auth-filters';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { DateValue } from 'app/shared/lib/date-time';
import { AuthSearchFormCustomElement } from 'app/um/components/common/auth-search-form';
import { openCorrespondenceHistory } from 'app/pp/components/correspondence/correspondence-history';
import { openAddServiceModal } from 'app/pp/components/modify/add-service';
import { AuthContext, IAuthContextSettings } from 'app/um/lib/auth-context';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { MyAuthorizationsReloadEvent } from 'app/pp/lib/events';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { IAuthMedicationsGridOptions } from 'app/um/components/auth/medications/auth-medications-grid';
import { IServiceGridOptions } from 'app/um/components/auth/service-grid';
import { requiredFields, requiredPlanFields } from 'app/cd/elements/members/member-display';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { NamespaceOptions } from 'app/shared/lib/i18n';
import { ITrackAuth } from 'app/um/services/tracking-service';
import { QueueUserTracker, TrackingQueue } from 'app/um/components/tracking/queue-user-tracker';
import { UpdateAuthRequest } from 'app/pp/components/update/update-auth-request';
import * as UM from 'app/um/models/essette-api';

interface IAuthDetailSettings {
    visible: boolean;
    contextSettings: IAuthContextSettings;
}

@autoinject()
export class SearchPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public tempAuthSearchFilters: IAuthSearchFilters = {};
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public tempMemberSearchFilters: IMemberSearchFilters = {};

    protected authSearchFilters: IAuthSearchFilters = {}
    protected authSearchOptions: Partial<IAuthSearchOptions> = {}
    protected memberSearchFilters: IMemberSearchFilters = {};
    protected apiSettings: IApiSettings = {};

    protected authResults: ITrackAuth[] = [];
    // Fields for Tracking Users in Auths
    protected fallbackPortalQueueId: string | null = null;
    protected trackingQueue: TrackingQueue | null = null;
    @observable
    protected trackedAuths: ITrackAuth[] | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;

    protected usePlaceholder: boolean = false
    protected selectedAuth: UM.Auth | null = null;

    protected hasPageAccess: boolean = false;
    protected hasAuthRoute: boolean = false;
    protected hasAuthModifyRoute: boolean = false;
    protected showAuthSummaryLink: boolean = false;
    protected showProviderSummaryLink: boolean = false;
    protected showPatientSummaryLink: boolean = false;
    protected canViewCorrespondence: boolean = false;
    protected canAddService: boolean = false;
    protected canSubmitAuthChangeRequests: boolean = false;

    protected authDetails: Record<string, IAuthDetailSettings> = {};

    protected authMedicationsGridOptions: IAuthMedicationsGridOptions = {
        showStatusReason: true,
        showQtyApproved: true,
        showDaysApproved: false,
        showApprovedWithRequestedCharges: true,
        showWarnings: false,
        showMinimalQuantityRequested: true,
        showMinimalQuantityApproved: true
    };

    protected serviceGridOptions: IServiceGridOptions = {
        canOpenServiceStatusHistory: false,
        canSyncToClaimsSystems: false,
        showDueDate: false,
        showServiceStatusReason: true,
        canApplyBulkServiceStatus: false,
        canApplyCorrespondenceRules: false,
        showExternalActions: false,
        showApprovedWithRequestedCharges: true,
        canSyncToClaimsSystemsLineLevel: false,
        showExistingAuthsWarning: false,
        showDuplicateAuths: false,
        showAddDocumentButton: true,
        showOpenIqcSummary: false,
        showWarnings: false,
        showModifiers: false,
        showQuantityApproved: true,
        showMinimalQuantityRequested: true,
        showMinimalQuantityApproved: true
    };

    // Subscription
    protected myAuthorizationsReload: Subscription | null = null;

    protected openAuthSummary = openAuthSummary;
    protected openCorrespondenceHistory = openCorrespondenceHistory;
    protected openAddServiceModal = openAddServiceModal;

    protected authAvailableFilters = ['number', 'class', 'class.subclass', 'status', 'requestedDateTime', 'isPending', 'openOnly', 'closedOnly'];
    protected memberAvailableFilters = ['identifier.primary', 'identifier.medicaid', 'name.first', 'name.last'];

    protected i18nNamespace: NamespaceOptions = NamespaceOptions.PP;

    ////****************** Element References ***************************************////

    protected $pager!: EssPagerCustomElement;
    protected $authSearch!: AuthSearchFormCustomElement;
    protected $authTable!: EssTableCustomElement<UM.Auth>;
    protected $updateAuthRequestModal!: EssModalCustomElement;
    protected $updateAuthRequest!: UpdateAuthRequest;
    protected $queueUserTracker!: QueueUserTracker;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly authService: AuthService,
        private readonly context: AuthContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate(): Promise<boolean> {
        await this.context.loadBasicSettings();
        await this.checkPermissions();
        return this.hasPageAccess;
    }

    protected async activate(): Promise<void> {
        const now: DateValue = new DateValue(this.env.today);
        this.authSearchFilters.requestedDateStart = now.add(-2, 'weeks').toString();
        this.authSearchFilters.requestedDateEnd = now.toString();
        await this.loadDefaultPortalQueue();
    }

    protected async attached(): Promise<void> {
        if(this.canSubmitAuthChangeRequests) this.$queueUserTracker.reload();
        this.myAuthorizationsReload = this.events.subscribe(MyAuthorizationsReloadEvent, (e: MyAuthorizationsReloadEvent) => {
            this.searchAuths();
        });
        await this.searchAuths();
    }

    protected detached(): void {
        if(this.myAuthorizationsReload) {
            this.myAuthorizationsReload.dispose();
        }
    }

    ////****************** Events ***************************************************////
    // New Tracked Auth changes, set the auth.users to the tracked auth users for the user-tracker icons.
    protected async trackedAuthsChanged(newValue: ITrackAuth[]): Promise<void> {
        if(newValue) {
            const trackedAuths = (newValue && newValue.length > 0) ? newValue : [];

            this.authResults.forEach(a => {
                const trackedAuth = trackedAuths.find(ta => ta.id === a.id);
                if(trackedAuth) a.users = trackedAuth.users;
                else a.users = [];
            });
        }
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.searchAuths();
    }

    protected async authDetailLoaded(event: ExpandEvent<UM.Auth>): Promise<void> {
        const item = event.detail.item;
        const items = event.detail.items;

        if(!item || !item.id) return;

        if(item && items.includes(item)) {
            if(!this.authDetails[item.id!]) {
                const contextSettings = await this.loadBasicSettings(item.id);
                this.authDetails[item.id!] = { visible: true, contextSettings: { ...contextSettings } };
            }
        }
    }

    protected openUpdateAuthRequestModal(auth: UM.Auth): void {
        if(!auth) return;
        this.selectedAuth = auth;
        this.$updateAuthRequestModal.open();
    }

    protected async cancelUpdateAuthRequest(): Promise<void> {
        if(this.$updateAuthRequest && this.$updateAuthRequest.hasPendingChanges()) {
            const result = await confirmation.showCancelChanges('um:auth.$');
            if(result.dialogResponse !== confirmation.options.OK) return;
        }

        this.selectedAuth = null;
        this.$updateAuthRequestModal.close();
    }

    ////****************** Functions ************************************************////
    // Build track users queue to pass to queue-user-tracker if user has permission to submit auth change requests
    private buildTrackUsersQueue(): void {
        if(!this.canSubmitAuthChangeRequests) return;

        const trackedAuthResults = this.authResults.filter(auth => !auth.isPending && auth.status?.flag?.allowEditsInProviderPortal);
        if(this.fallbackPortalQueueId) {
            this.trackingQueue = {
                id: this.fallbackPortalQueueId,
                auths: trackedAuthResults,
                queueType: 'portal'
            }
        }
        else {
            this.trackingQueue = null
        }
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.hasPageAccess = this.accountContext.hasRouteAccess('pp.auths.search');
        this.hasAuthRoute = this.accountContext.hasRouteAccess('pp.auths.create');
        this.hasAuthModifyRoute = this.accountContext.hasRouteAccess('pp.auths.modify');
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('pp.auth.summary');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
        this.showPatientSummaryLink = this.accountContext.hasRouteAccess('pp.patient.summary');
        this.canViewCorrespondence = this.accountContext.hasPermission('UM.AuthCorrespondenceLetter.Download.Preview');
        this.canAddService = this.context.portalAddServices && this.accountContext.hasPermission('UM.Auth.InsertDetails.RequestService');
        this.canSubmitAuthChangeRequests = this.accountContext.hasPermission('UM.Auth.Reopen.ReopenAuth');
    }

    protected clearResults() {
        this.authResults = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
    }

    protected checkLetterCopies(auth: UM.Auth): boolean {
        return auth.letters?.some((letter) => {
            const flag = letter.status?.flag;
            return (letter.isActive && flag?.isClosed === true && flag.isSuccessful === true && flag.triggersCopies == false);
        }) || false;
    }

    protected hasAuthObjectConfig(auth: UM.Auth): boolean {
        return auth.class?.subClass?.objects?.some((o) => (o.code == 'Procedure' || o.code == 'DentalProcedure') && o.detail?.displayInPortal == true) || false;
    }

    protected async submitAuthSearch() {
        this.authSearchFilters = { ...this.tempAuthSearchFilters };
        this.memberSearchFilters = { ...this.tempMemberSearchFilters };
        await this.searchAuths();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDefaultPortalQueue(): Promise<void> {
        if(!this.canSubmitAuthChangeRequests) return;
        try {
            this.state.beginLoad();

            const response = await this.authService.loadPortalQueues({ all: true }, undefined, ['id'], undefined, true);
            this.fallbackPortalQueueId = response.results[0]?.id || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async searchAuths(): Promise<void | null> {
        const options: StandardLoadOptions = {
            includeInactive: false,
            top: this.pageSize,
            sorting: this.sorting,
            page: this.$pager?.page || 0
        }

        const includes: string[] = [
            'class',
            'class.subClass',
            'class.subClass.assessments',
            'healthcareServiceType',
            'letters',
            'letters.status',
            'member',
            'member.plan',
            'placeOfService',
            'preferredContact',
            'status',
            'type'
        ];

        const fields: string[] = [
            'class.description',
            'class.subClass.description',
            'healthcareServiceType.description',
            'isAdmission',
            'isPending',
            'letters.status.flag.isClosed',
            'letters.status.flag.isSuccessful',
            'letters.status.flag.triggersCopies',
            'number',
            'placeOfService.description',
            'preferredContact.emailAddress',
            'preferredContact.fax.*',
            'preferredContact.name',
            'preferredContact.phone.*',
            'requestedDateTime',
            'status.description',
            'status.flag.allowEditsInProviderPortal',
            'type.description',
            'type.flag.usesPreserviceDay'
        ];
        fields.push(...requiredFields.map(field => `member.${field}`));
        fields.push(...requiredPlanFields.map(field => `member.plan.${field}`));

        try {
            this.state.beginLoad();
            const response = await this.authService.searchAuths(this.authSearchFilters, this.memberSearchFilters, options, includes, fields);

            this.authResults = response.results;
            // update selected auth with latest loaded data in case Update Auth Request modal is open and auth summary button is available
            if(this.selectedAuth && this.showAuthSummaryLink) {
                const updatedSelectedAuth = this.authResults.find(a => a.id === this.selectedAuth?.id);
                if(updatedSelectedAuth) this.selectedAuth = updatedSelectedAuth;
            }
            this.resultCount = response.count || 0;
            this.usePlaceholder = this.canSubmitAuthChangeRequests && this.authResults.some(auth => auth.status?.flag?.allowEditsInProviderPortal);
            if(this.$authSearch) this.$authSearch.showSelectedCriteria();

            this.buildTrackUsersQueue();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async loadBasicSettings(authId: string): Promise<IAuthContextSettings> {
        let settings: IAuthContextSettings;
        try {
            const response = await this.authService.loadAuthSettings(authId);
            const apiSettings = response.settings;
            settings = this.context.getSettings(apiSettings);
        }
        catch(error) {
            throw error;
        }
        return settings;
    }
}
